/**
 * Staff sign-in against the portal project's Supabase Auth (the same login staff use on the
 * portal; Google or email and password). The desk holds only the portal's public key. The API
 * verifies the access token on every call; this module only obtains and refreshes it.
 */
import { createClient, isAuthRetryableFetchError, type Session, type SupabaseClient } from '@supabase/supabase-js';
import { useSyncExternalStore } from 'react';

const URL_ = import.meta.env['VITE_PORTAL_SUPABASE_URL'] as string | undefined;
const KEY = import.meta.env['VITE_PORTAL_SUPABASE_ANON_KEY'] as string | undefined;

export const authConfigured = Boolean(URL_ && KEY);
const supabase: SupabaseClient | null = URL_ && KEY ? createClient(URL_, KEY, { auth: { persistSession: true, autoRefreshToken: true, detectSessionInUrl: true } }) : null;

export const STAFF_DOMAIN = '@lifelongmerchantservices.com';
const REFUSAL_KEY = 'lsc.refusal';

interface AuthState {
  session: Session | null;
  loading: boolean;
}
let state: AuthState = { session: null, loading: authConfigured };
const listeners = new Set<() => void>();
const set = (next: AuthState) => {
  state = next;
  for (const fn of listeners) fn();
};

if (supabase) {
  void supabase.auth.getSession().then(({ data }) => {
    set({ session: data.session, loading: false });
  });
  supabase.auth.onAuthStateChange((_event, session) => {
    set({ session, loading: false });
  });
}

export function useSession(): AuthState {
  return useSyncExternalStore(
    (fn) => {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
    () => state,
    () => state,
  );
}

export interface DeskActor {
  name: string | null;
  email: string;
}

export function useActor(): DeskActor | null {
  const { session } = useSession();
  if (!session) return null;
  const meta = session.user.user_metadata as Record<string, unknown>;
  const name = typeof meta['full_name'] === 'string' ? meta['full_name'] : typeof meta['name'] === 'string' ? meta['name'] : null;
  return { name, email: session.user.email ?? '' };
}

/** The current access token, refreshed by the client when it is close to expiry. */
/** Seconds until a JWT's `exp`, or -1 when it cannot be read. */
export function secondsLeft(token: string, nowMs: number = Date.now()): number {
  try {
    const part = token.split('.')[1] ?? '';
    const json = JSON.parse(atob(part.replace(/-/g, '+').replace(/_/g, '/'))) as { exp?: unknown };
    return typeof json.exp === 'number' ? json.exp - nowMs / 1000 : -1;
  } catch {
    return -1;
  }
}

/** Renew a token this close to its end before sending it, so a request never lands on the edge. */
const RENEW_WITHIN_S = 60;

/**
 * The token to send. One within a minute of expiry is renewed first (2026-10-23: reps were
 * signed out exactly an hour after signing in, when a request raced the expiry).
 */
export async function accessToken(): Promise<string | null> {
  if (!supabase) return null;
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token ?? null;
  if (token && secondsLeft(token) < RENEW_WITHIN_S) {
    const r = await refreshAccessToken();
    if ('token' in r) return r.token;
  }
  return token;
}

/**
 * Force a refresh (the socket was told the token is stale). `rejected` means the session is gone;
 * `transient` means Supabase or the network failed and the caller should retry, not sign out.
 */
export async function refreshAccessToken(): Promise<{ token: string } | { error: 'rejected' | 'transient' }> {
  if (!supabase) return { error: 'rejected' };
  try {
    const { data, error } = await supabase.auth.refreshSession();
    if (error) {
      // The library's own timer, or another tab, may have just renewed the same session: the
      // renewal we sent then reads "already used", yet the session is fine. Look before giving up.
      const again = (await supabase.auth.getSession()).data.session?.access_token;
      if (again && secondsLeft(again) > 5) return { token: again };
      return { error: isAuthRetryableFetchError(error) || (error.status ?? 0) >= 500 || !error.status ? 'transient' : 'rejected' };
    }
    return data.session ? { token: data.session.access_token } : { error: 'rejected' };
  } catch {
    return { error: 'transient' };
  }
}

export async function signInWithGoogle(): Promise<{ error: string | null }> {
  if (!supabase) return { error: 'Sign-in is not configured on this build.' };
  const { error } = await supabase.auth.signInWithOAuth({ provider: 'google', options: { redirectTo: window.location.origin } });
  return { error: error?.message ?? null };
}

export async function signInWithPassword(email: string, password: string): Promise<{ error: string | null }> {
  if (!supabase) return { error: 'Sign-in is not configured on this build.' };
  const { error } = await supabase.auth.signInWithPassword({ email, password });
  if (!error) return { error: null };
  // Only a real credential mismatch says so; anything else (the portal's captcha, a rate limit, an
  // outage) used to read as a wrong password, which hid the captcha for two days (2026-10-24).
  if (error.code === 'invalid_credentials') return { error: 'That email and password did not match.' };
  if (error.code === 'captcha_failed') return { error: 'Password sign-in is blocked by a security check on this account system. Use Google.' };
  return { error: `Sign-in did not go through (${error.code ?? String(error.status ?? 'no answer')}). Try again, or use Google.` };
}

/** Sign out, optionally recording why so the login screen can say it. */
export async function signOut(reason?: string): Promise<void> {
  try {
    if (reason) sessionStorage.setItem(REFUSAL_KEY, reason);
  } catch {
    /* storage off: the reason is lost, the sign-out still happens */
  }
  if (supabase) await supabase.auth.signOut();
  else set({ session: null, loading: false });
}

/** The recorded reason, read once. */
export function takeRefusal(): string | null {
  try {
    const r = sessionStorage.getItem(REFUSAL_KEY);
    sessionStorage.removeItem(REFUSAL_KEY);
    return r;
  } catch {
    return null;
  }
}
